import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCopy, faPlay } from '@fortawesome/free-solid-svg-icons';
import {
  Icon,
  UilFilesLandscapesAlt,
  UilBook,
  UilUserSquare,
  UilSuitcase
} from '@iconscout/react-unicons';
import { Permission, UserType } from 'reducers/UserReducer';

export enum RouteId {
  // Shared sections
  CONTROL_CENTER = 'control-center',
  ACCOUNT = 'account',

  // Candidate sections
  CAREER_PATHWAYS = 'career-pathways',

  // Institution sections
  ACADEMY = 'academy',

  // Recruiter sections
  RECRUITMENT = 'recruitment',

  // Candidate pages
  HOME = 'home',
  TALENT_LADDER = 'talent-ladder',
  TALENT_LADDER_BASICS = 'talent-ladder-basics',
  TALENT_LADDER_LOGIC = 'logic',
  TALENT_LADDER_PERSONALITY = 'talent-ladder-personality',
  JOBS = 'jobs',
  PROFILE = 'profile',

  // Institution pages
  HUB_DASHBOARD = 'hub-dashboard',
  STUDENTS = 'students',
  USERS = 'users',
  HUB_PROFILE = 'hub-profile',

  // Recruiter pages
  PANEL_DASHBOARD = 'panel-dashboard',
  RECRUITMENT_SEARCH = 'recruitment-search',
  RECRUITMENT_CAREERS = 'recruitment-careers',
  PANEL_PROFILE = 'panel-profile'
}

export interface Route {
  id: RouteId;
  name: string;
  icon?: IconProp | string | string[];
  iconSet?: 'font-awesome' | 'feather' | 'unicons';
  pages?: Route[];
  path?: string;
  pathName?: string;
  flat?: boolean;
  topNavIcon?: string;
  dropdownInside?: boolean;
  active?: boolean;
  new?: boolean;
  hasNew?: boolean;
  next?: boolean;
  accessEntity?: string;
}

export interface RouteItems {
  id: RouteId;
  label: string;
  horizontalNavLabel?: string;
  icon: Icon;
  labelDisabled?: boolean;
  pages: Route[];
  megaMenu?: boolean;
  active?: boolean;
}

export interface LogicRoute {
  path: string;
  label: string;
  icon?: IconProp;
}

const routesMap: { [key in UserType]: RouteItems[] } = {
  admin: [],
  candidate: [
    {
      id: RouteId.CONTROL_CENTER,
      label: 'Control Center',
      icon: UilFilesLandscapesAlt,
      pages: [
        {
          id: RouteId.HOME,
          name: 'Home',
          icon: 'home',
          path: '/home/dashboard',
          pathName: 'home',
          active: true
        }
      ]
    },
    {
      id: RouteId.CAREER_PATHWAYS,
      label: 'Career Pathways',
      icon: UilSuitcase,
      pages: [
        {
          id: RouteId.TALENT_LADDER,
          name: 'talent-ladder',
          icon: 'star',
          active: true,
          hasNew: true,
          pages: [
            {
              id: RouteId.TALENT_LADDER_BASICS,
              name: 'Basics',
              path: '/home/ladder/basic',
              pathName: 'basic',
              active: true
            },
            {
              id: RouteId.TALENT_LADDER_LOGIC,
              name: 'Logic',
              path: '/home/ladder/logic',
              pathName: 'logic',
              active: false
            },
            {
              id: RouteId.TALENT_LADDER_PERSONALITY,
              name: 'Personality',
              path: '/home/ladder/personality',
              pathName: 'personality',
              active: false
            }
          ]
        },
        {
          id: RouteId.JOBS,
          name: 'Jobs',
          icon: 'briefcase',
          path: '/home/jobs',
          pathName: 'jobs',
          active: true
        }
      ]
    },
    {
      id: RouteId.ACCOUNT,
      label: 'Account',
      icon: UilUserSquare,
      pages: [
        {
          id: RouteId.PROFILE,
          name: 'Profile',
          icon: 'user',
          path: '/home/profile',
          pathName: 'profile',
          active: true
        }
      ]
    }
  ],
  institution: [
    {
      id: RouteId.CONTROL_CENTER,
      label: 'Control Center',
      icon: UilFilesLandscapesAlt,
      pages: [
        {
          id: RouteId.HUB_DASHBOARD,
          name: 'Dashboard',
          icon: 'pie-chart',
          path: '/hub/dashboard',
          pathName: 'dashboard',
          active: true
        }
      ]
    },
    {
      id: RouteId.ACADEMY,
      label: 'Academy',
      icon: UilBook,
      pages: [
        {
          id: RouteId.STUDENTS,
          name: 'Students',
          icon: 'users',
          path: '/hub/students',
          pathName: 'students',
          active: true,
          accessEntity: 'student'
        }
      ]
    },
    {
      id: RouteId.ACCOUNT,
      label: 'Account',
      icon: UilUserSquare,
      pages: [
        {
          id: RouteId.USERS,
          name: 'Users',
          icon: 'users',
          path: '/hub/users',
          pathName: 'users',
          active: true,
          accessEntity: 'user'
        },
        {
          id: RouteId.HUB_PROFILE,
          name: 'Profile',
          icon: 'user',
          path: '/hub/profile',
          pathName: 'profile',
          active: true,
          accessEntity: 'profile'
        }
      ]
    }
  ],
  recruiter: [
    {
      id: RouteId.CONTROL_CENTER,
      label: 'Control Center',
      icon: UilFilesLandscapesAlt,
      pages: [
        {
          id: RouteId.PANEL_DASHBOARD,
          name: 'Dashboard',
          icon: 'pie-chart',
          path: '/panel/dashboard',
          pathName: 'dashboard',
          active: true
        }
      ]
    },
    {
      id: RouteId.RECRUITMENT,
      label: 'Recruitment',
      icon: UilBook,
      pages: [
        {
          id: RouteId.RECRUITMENT_SEARCH,
          name: 'Search',
          icon: 'search',
          path: '/panel/recruitment/search',
          pathName: 'students',
          active: true
        },
        {
          id: RouteId.RECRUITMENT_CAREERS,
          name: 'Jobs / Internships',
          icon: 'briefcase',
          path: '/panel/recruitment/careers',
          pathName: 'students',
          active: true
        }
        // {
        //   name: 'Interviews',
        //   icon: 'tablet',
        //   path: '/panel/recruitment/interviews',
        //   pathName: 'students',
        //   active: true
        // }
      ]
    },
    {
      id: RouteId.ACCOUNT,
      label: 'Account',
      icon: UilUserSquare,
      pages: [
        {
          id: RouteId.PANEL_PROFILE,
          name: 'Profile',
          icon: 'user',
          path: '/panel/profile',
          pathName: 'profile',
          active: true
        }
      ]
    }
  ]
};

// export const routes: RouteItems[] = [
//   {
//     label: 'Control Center',
//     icon: UilFilesLandscapesAlt,
//     pages: [
//       {
//         name: 'Dashboard',
//         icon: 'pie-chart',
//         path: '/dashboard',
//         pathName: 'dashboard',
//         active: true
//       }
//     ]
//   }
// ];

export function useRoute(
  permission: Permission[],
  userType?: UserType
): RouteItems[] {
  let route: RouteItems[] = [];
  if (userType) {
    const cloneRoute = [...routesMap[userType]];
    if (userType === 'candidate' && permission && permission.length > 0) {
      const careerSectionIndex = cloneRoute.findIndex(
        r => r.id === RouteId.CAREER_PATHWAYS
      );
      const careerSection = cloneRoute[careerSectionIndex];
      const talentLadderIndex = careerSection.pages.findIndex(
        r => r.id === RouteId.TALENT_LADDER
      );
      const talentLadder = careerSection.pages[talentLadderIndex].pages?.map(
        t => ({
          ...t,
          active: !t.active
            ? Boolean(permission.find(p => p.route === t.id)?.permit)
            : t.active
        })
      );
      careerSection.pages[talentLadderIndex].pages = talentLadder;
      cloneRoute[careerSectionIndex] = careerSection;
    }
    route = cloneRoute;
  }
  return route;
}

export function useLogicChallengeRoute(): LogicRoute[] {
  return [
    {
      label: 'Explorer',
      path: '/assessment/challenge/logic/explorer',
      icon: faCopy
    },
    {
      label: 'Run and Debug',
      path: '/assessment/challenge/logic/run',
      icon: faPlay
    }
  ];
}
